import React, { useEffect, useState, useCallback, useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { UserPlus, Pencil, Trash2, PhoneCall, ArrowRightCircle, CheckCircle2, Search, QrCode, Printer, UserCog, Users, TrendingUp, HeartHandshake, Sparkles, ScanLine, Building2, UserCheck, ChevronDown, Footprints, BellRing, Clock, GripVertical, History } from "lucide-react";
import { usePaged, Paginator } from "../components/Paginator";
import { LOGO_DARK } from "../assets/brand";
import { useAuth } from "../context/AuthContext";
import PhoneActions from "../components/PhoneActions";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";
const INPUT = "w-full rounded-xl border border-[#EBE9E4] px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0B5E4E]/30";
const CONTACT_TYPES = [["evangelism", "Evangelism / Outreach"], ["event_visitor", "Event Visitor"], ["other", "Other"]];
const EMPTY = { name: "", phone: "", email: "", contact_type: "", contact_type_other: "", address: "", postcode: "", notes: "" };
const withContactType = (c) => {
  if (c.contact_type && c.contact_type !== "other") return { ...c, contact_type_other: "" };
  const h = (c.how_heard || "").trim();
  const known = CONTACT_TYPES.find(([, l]) => l === h);
  if (known) return { ...c, contact_type: known[0], contact_type_other: "" };
  return { ...c, contact_type: h ? "other" : "", contact_type_other: h.replace(/^Other:\s*/, "") };
};

const TAB_META = {
  evangelism: { label: "Contacts", full: "Evangelism Contacts", addLabel: "Add evangelism contact", blurb: "People met during evangelism outreach. Move them to Follow-up, or straight to Pastoral Care when ready." },
  evangelism_follow_up: { label: "Follow-up", full: "Evangelism Follow-up", addLabel: "Add follow-up contact", blurb: "Contacts being followed up after outreach. Assign a caller, log your calls, then move to Pastoral Care by hand." },
  visitor_care: { label: "Contacts", full: "Visitor Contacts", addLabel: "Add visitor", blurb: "Event visitors and people just visiting. Make a welcome call, move them to Follow-up, or to Pastoral Care when ready." },
  visitor_follow_up: { label: "Follow-up", full: "Visitor Follow-up", addLabel: "Add visitor to follow up", blurb: "Visitors being followed up. Assign a caller, log your calls, then move to Pastoral Care by hand." },
  handed_off: { label: "Pastoral", full: "Pastoral Care", addLabel: "", blurb: "Moved to Pastoral Care (member records created)." },
};

const SECTIONS = {
  evangelism: {
    title: "Evangelism", sub: "Evangelism contacts and follow-ups. Nobody joins Members or Pastoral Care until you move them.",
    stages: ["evangelism", "evangelism_follow_up"], followUp: "evangelism_follow_up",
    stats: [
      { key: "new_this_month", label: "New this month", icon: Sparkles },
      { key: "in_evangelism", label: "Contacts", icon: Users },
      { key: "in_evangelism_follow_up", label: "In follow-up", icon: Footprints },
      { key: "evangelism_awaiting", label: "Awaiting call", icon: PhoneCall },
      { key: "evangelism_handed_off", label: "Moved to pastoral", icon: HeartHandshake },
      { key: "via_qr", label: "Via QR poster", icon: ScanLine },
      { key: "added_by_staff", label: "Added by staff", icon: Building2 },
      { key: "conversion_rate", label: "Conversion rate", icon: TrendingUp, suffix: "%" },
    ],
  },
  visitor: {
    title: "Visitor Care", sub: "Event visitors and people just visiting. Nobody joins Members or Pastoral Care until you move them.",
    stages: ["visitor_care", "visitor_follow_up"], followUp: "visitor_follow_up",
    stats: [
      { key: "visitor_new_this_month", label: "New this month", icon: Sparkles },
      { key: "in_visitor_care", label: "Contacts", icon: Users },
      { key: "in_visitor_follow_up", label: "In follow-up", icon: Footprints },
      { key: "visitor_awaiting", label: "Awaiting call", icon: PhoneCall },
      { key: "visitor_handed_off", label: "Moved to pastoral", icon: HeartHandshake },
    ],
  },
};

export default function Outreach({ section = "evangelism" }) {
  const SEC = SECTIONS[section];
  const STAT_META = SEC.stats;
  const { user } = useAuth();
  const [stages, setStages] = useState([]);
  const [tab, setTab] = useState("");
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [assignees, setAssignees] = useState([]);
  const [unassigned, setUnassigned] = useState(0);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null); // {mode, data}
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [callFor, setCallFor] = useState(null);
  const [callNote, setCallNote] = useState("");
  const [assignFor, setAssignFor] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [mineOnly, setMineOnly] = useState(false);
  const [workloadOpen, setWorkloadOpen] = useState(false);
  const [view, setView] = useState("list"); // "list" | "board"
  const [showPastoral, setShowPastoral] = useState(false);
  const [slaDays, setSlaDays] = useState(3);
  const [dragId, setDragId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const qrRef = useRef(null);
  const connectUrl = `${window.location.origin}${section === "visitor" ? "/first-timers" : "/connect"}`;
  const qrTitle = section === "visitor" ? "First Timers & Visitors — Connect with us" : "Evangelism — Connect with us";
  const connectQrUrl = `${connectUrl}?src=qr`;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/outreach");
      const mine = SEC.stages.filter((st) => (data.stages || []).includes(st));
      setStages(mine);
      setContacts((data.contacts || []).filter((c) => SEC.stages.includes(c.stage) || (c.stage === "handed_off" && (c.stages_seen || []).some((x) => SEC.stages.includes(x)))));
      setShowPastoral(!!data.show_pastoral_column);
      setSlaDays(data.sla_days || 3);
      setTab((t) => (mine.includes(t) ? t : mine[0] || ""));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    try { const { data } = await api.get("/outreach/stats"); setStats(data); } catch {}
  }, [SEC]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (["visitor_care", "visitor_follow_up", "evangelism_follow_up"].includes(tab)) {
      api.get(`/outreach/assignees?stage=${tab}`)
        .then(({ data }) => { setAssignees(data.assignees || []); setUnassigned(data.unassigned || 0); })
        .catch(() => { setAssignees([]); setUnassigned(0); });
    } else { setAssignees([]); setUnassigned(0); }
  }, [tab, contacts]);

  const callStage = ["visitor_care", "visitor_follow_up", "evangelism_follow_up"].includes(tab);
  const toPastoral = (c) => {
    if (!window.confirm(`Move ${c.name} to Pastoral Care? This creates their member record.`)) return;
    act(`/outreach/${c.id}/handoff-pastoral`, `${c.name} moved to Pastoral Care`);
  };

  const rows = contacts.filter((c) => {
    const isCurrent = c.stage === tab;
    const isHistorical = showHistory && !isCurrent && (c.stages_seen || []).includes(tab);
    if (!isCurrent && !isHistorical) return false;
    if (isCurrent && callStage && mineOnly && c.assigned_to_id !== user?.id) return false;
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [c.name, c.phone, c.email, c.how_heard, c.assigned_to_name].some((v) => (v || "").toLowerCase().includes(s));
  });
  const rowsPaged = usePaged(rows, 25);

  const save = async (e) => {
    e.preventDefault();
    if (!form.data.name.trim()) return toast.error("Name is required.");
    if (!form.data.contact_type) return toast.error("Please choose a contact type.");
    if (form.data.contact_type === "other" && !form.data.contact_type_other.trim()) return toast.error("Please type the contact type.");
    setSaving(true);
    try {
      if (form.mode === "edit") {
        await api.put(`/outreach/${form.data.id}`, form.data);
        toast.success("Contact updated");
      } else {
        await api.post(`/outreach?stage=${tab}`, form.data);
        toast.success(form.data.contact_type === "event_visitor" && section !== "visitor" ? "Event visitor added to Visitor Care" : "Contact added");
      }
      setForm(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const act = async (url, msg) => {
    try { await api.post(url); toast.success(msg); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const logCall = async () => {
    try {
      await api.post(`/outreach/${callFor.id}/contact`, { note: callNote, outcome: "reached" });
      toast.success("Call logged");
      setCallFor(null); setCallNote(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const assign = async (assigneeId) => {
    try {
      await api.post(`/outreach/${assignFor.id}/assign`, { assignee_id: assigneeId });
      toast.success(assigneeId ? "Caller assigned" : "Assignment cleared");
      setAssignFor(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const del = async () => {
    try { await api.delete(`/outreach/${confirmDel.id}`); toast.success("Deleted"); setConfirmDel(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const moveContact = async (c, target) => {
    if (!c || c.stage === target) return;
    if (c.stage === "handed_off") {
      if (!window.confirm(`Move ${c.name} back out of Pastoral? This removes the member record created at handoff.`)) return;
    }
    if (target === "handed_off" && !window.confirm(`Hand off ${c.name} to the Pastoral team? This creates their member record.`)) return;
    try {
      await api.post(`/outreach/${c.id}/move`, { stage: target });
      toast.success(`${c.name} moved to ${TAB_META[target]?.full || "Pastoral"}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const runSlaNudge = async () => {
    try {
      const { data } = await api.post("/outreach/sla-nudge");
      toast.success(data.nudged ? `Reminded callers about ${data.nudged} overdue contact(s)` : "No overdue contacts right now 🎉");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const printQr = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const img = canvas.toDataURL("image/png");
    const w = window.open("", "_blank", "width=600,height=800");
    if (!w) return toast.error("Please allow pop-ups to print.");
    w.document.write(`<!doctype html><html><head><title>${qrTitle} — Streams of Joy Erith London</title>
      <style>
        *{box-sizing:border-box;margin:0}
        body{font-family:'Plus Jakarta Sans',Arial,sans-serif;color:#1E2A28;text-align:center;padding:56px 40px}
        img.logo{height:70px;object-fit:contain;margin-bottom:28px}
        h1{font-size:34px;font-weight:800;color:#0B5E4E;line-height:1.15;margin-bottom:10px}
        p.sub{font-size:17px;color:#4B5A57;margin-bottom:34px}
        .qr{display:inline-block;padding:22px;border:3px solid #0B5E4E;border-radius:24px}
        .qr img{width:300px;height:300px;display:block}
        p.url{margin-top:26px;font-size:20px;font-weight:700;color:#0B5E4E}
        p.foot{margin-top:14px;font-size:14px;color:#8A8A8A}
      </style></head><body>
      <img class="logo" src="${LOGO_DARK}" onerror="this.style.display='none'"/>
      <h1>${section === "visitor" ? "First time or visiting? Let's connect" : "Let's connect"}</h1>
      <p class="sub">${section === "visitor" ? "Scan to tell us you're new here or just visiting. We'd love to welcome you to Streams of Joy Erith London." : "Scan to say hello. We'd love to stay in touch from Streams of Joy Erith London."}</p>
      <div class="qr"><img src="${img}"/></div>
      <p class="url">${connectUrl.replace(/^https?:\/\//, "")}</p>
      <p class="foot">Point your phone camera at the code and tap the link.</p>
      </body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 400);
  };

  const meta = TAB_META[tab] || {};

  return (
    <div className="space-y-6" data-testid="outreach-page">
      <div className="sj-page-hero flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="font-head text-3xl font-extrabold" style={{ color: "var(--sj-text)" }} data-testid="outreach-title">{SEC.title}</h1>
          <p className="mt-1 text-sm" style={{ color: "var(--sj-text-2)" }}>{SEC.sub}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <div className="flex rounded-full p-1 border" style={{ borderColor: "var(--sj-border)", background: "#fff" }} data-testid="outreach-view-toggle">
            <button data-testid="outreach-view-list" onClick={() => setView("list")}
              className="px-3.5 py-1.5 rounded-full text-sm font-semibold transition-colors"
              style={view === "list" ? { background: "var(--sj-primary)", color: "#fff" } : { color: "var(--sj-text-2)" }}>List</button>
            <button data-testid="outreach-view-board" onClick={() => setView("board")}
              className="px-3.5 py-1.5 rounded-full text-sm font-semibold transition-colors"
              style={view === "board" ? { background: "var(--sj-primary)", color: "#fff" } : { color: "var(--sj-text-2)" }}>Board</button>
          </div>
          {(user?.role === "admin" || user?.role === "pastor" || user?.caps?.manage_members) && (
            <button data-testid="outreach-sla-btn" onClick={runSlaNudge}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold hover:bg-[var(--sj-bg)]" style={{ color: "var(--sj-primary)", border: "1px solid var(--sj-border)", background: "#fff" }}>
              <BellRing className="h-4 w-4" /> Remind overdue
            </button>
          )}
          <button data-testid="outreach-qr-btn" onClick={() => setQrOpen(true)}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold hover:bg-[var(--sj-bg)]" style={{ color: "var(--sj-primary)", border: "1px solid var(--sj-border)", background: "#fff" }}>
            <QrCode className="h-4 w-4" /> Print Connect QR
          </button>
        </div>
      </div>

      {/* Pipeline stats */}
      {stats && (
        <div className={`grid grid-cols-2 md:grid-cols-4 ${STAT_META.length > 5 ? "lg:grid-cols-8" : "lg:grid-cols-5"} gap-3`} data-testid="outreach-stats">
          {STAT_META.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.key} className={`${CARD} p-4`} data-testid={`outreach-stat-${s.key}`}>
                <div className="flex items-center gap-2 mb-1">
                  <div className="h-7 w-7 rounded-lg flex items-center justify-center" style={{ background: "#0B5E4E1A" }}>
                    <Icon className="h-3.5 w-3.5" style={{ color: "var(--sj-primary)" }} />
                  </div>
                  <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: "var(--sj-text-3)" }}>{s.label}</span>
                </div>
                <div className="font-head text-2xl font-extrabold" style={{ color: "var(--sj-text)" }}>
                  {stats[s.key] ?? 0}{s.suffix || ""}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {view === "list" && (<>
      {stages.length > 1 && (
        <div className="flex gap-2">
          {stages.map((s) => (
            <button key={s} data-testid={`outreach-tab-${s}`} onClick={() => setTab(s)}
              className="px-4 py-2 rounded-full text-sm font-semibold transition-colors"
              style={tab === s ? { background: "var(--sj-primary)", color: "#fff" } : { background: "#fff", color: "var(--sj-text-2)", border: "1px solid var(--sj-border)" }}>
              {TAB_META[s]?.label || s}
            </button>
          ))}
        </div>
      )}

      {callStage && assignees.length > 0 && (
        <div className={`${CARD} p-5`} data-testid="outreach-workload">
          <button type="button" data-testid="outreach-workload-toggle" onClick={() => setWorkloadOpen((o) => !o)}
            className="w-full flex items-center gap-2 text-left">
            <UserCog className="h-4 w-4" style={{ color: "var(--sj-primary)" }} />
            <h2 className="font-head text-lg font-bold" style={{ color: "var(--sj-text)" }}>Caller workload</h2>
            <span className="text-xs" style={{ color: "var(--sj-text-3)" }}>· contacts currently in care</span>
            <ChevronDown className="h-4 w-4 ml-auto shrink-0 transition-transform" style={{ color: "var(--sj-text-3)", transform: workloadOpen ? "rotate(180deg)" : "none" }} />
          </button>
          {workloadOpen && (
          <div className="space-y-2.5 mt-3">
            {(() => {
              const maxLoad = Math.max(1, ...assignees.map((a) => a.assigned_count || 0), unassigned);
              const rowsW = [...assignees].sort((a, b) => (b.assigned_count || 0) - (a.assigned_count || 0));
              return (
                <>
                  {rowsW.map((a) => (
                    <div key={a.id} className="flex items-center gap-3" data-testid={`outreach-workload-${a.id}`}>
                      <span className="w-40 shrink-0 truncate text-sm font-medium" style={{ color: "var(--sj-text)" }}>{a.name}</span>
                      <div className="flex-1 h-2.5 rounded-full overflow-hidden" style={{ background: "var(--sj-bg)" }}>
                        <div className="h-full rounded-full transition-all" style={{ width: `${((a.assigned_count || 0) / maxLoad) * 100}%`, background: "var(--sj-primary)" }} />
                      </div>
                      <span className="w-8 text-right text-sm font-bold" style={{ color: "var(--sj-text-2)" }}>{a.assigned_count || 0}</span>
                    </div>
                  ))}
                  {unassigned > 0 && (
                    <div className="flex items-center gap-3 pt-2 mt-1 border-t" style={{ borderColor: "var(--sj-border)" }} data-testid="outreach-workload-unassigned">
                      <span className="w-40 shrink-0 truncate text-sm font-medium" style={{ color: "#D97706" }}>Unassigned</span>
                      <div className="flex-1 h-2.5 rounded-full overflow-hidden" style={{ background: "var(--sj-bg)" }}>
                        <div className="h-full rounded-full" style={{ width: `${(unassigned / maxLoad) * 100}%`, background: "#D97706" }} />
                      </div>
                      <span className="w-8 text-right text-sm font-bold" style={{ color: "#D97706" }}>{unassigned}</span>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
          )}
        </div>
      )}

      <div className={`${CARD} p-5`}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="font-head text-lg font-bold" style={{ color: "var(--sj-text)" }}>{meta.label}</h2>
            <p className="text-xs mt-0.5" style={{ color: "var(--sj-text-3)" }}>{meta.blurb}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {callStage && (
              <button data-testid="outreach-mine-toggle" onClick={() => setMineOnly((v) => !v)}
                className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition-colors"
                style={mineOnly ? { background: "var(--sj-primary)", color: "#fff" } : { background: "#fff", color: "var(--sj-text-2)", border: "1px solid var(--sj-border)" }}>
                <UserCheck className="h-4 w-4" /> Assigned to me
              </button>
            )}
            <button data-testid="outreach-history-toggle" onClick={() => setShowHistory((v) => !v)}
              title="Show contacts your team worked on that have since been handed over"
              className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition-colors"
              style={showHistory ? { background: "var(--sj-primary)", color: "#fff" } : { background: "#fff", color: "var(--sj-text-2)", border: "1px solid var(--sj-border)" }}>
              <History className="h-4 w-4" /> Show handed-over
            </button>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--sj-primary)" }} />
              <input data-testid="outreach-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className="bg-white rounded-full border-2 border-[#0B5E4E] shadow-sm pl-9 pr-4 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0B5E4E]/30" />
            </div>
            <button data-testid="outreach-add-btn" onClick={() => setForm({ mode: "add", data: { ...EMPTY } })}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white hover:brightness-105" style={{ background: "var(--sj-primary)" }}>
              <UserPlus className="h-4 w-4" /> {meta.addLabel}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead style={{ color: "var(--sj-text-2)", background: "var(--sj-bg)" }}>
              <tr>
                <th className="py-3 px-4 font-semibold uppercase tracking-wider text-xs">Name</th>
                <th className="py-3 px-4 font-semibold uppercase tracking-wider text-xs">Contact</th>
                <th className="py-3 px-4 font-semibold uppercase tracking-wider text-xs">Contact type</th>
                {callStage && <th className="py-3 px-4 font-semibold uppercase tracking-wider text-xs">Assigned to</th>}
                <th className="py-3 px-4 font-semibold uppercase tracking-wider text-xs">Added by</th>
                <th className="py-3 px-4 font-semibold uppercase tracking-wider text-xs">Status</th>
                <th className="py-3 px-4 font-semibold uppercase tracking-wider text-xs text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={callStage ? 7 : 6} className="py-10 text-center" style={{ color: "var(--sj-text-3)" }}>{callStage && mineOnly ? "No contacts assigned to you yet." : "No contacts yet."}</td></tr>
              )}
              {rowsPaged.pageItems.map((c) => { const hist = c.stage !== tab; return (
                <tr key={c.id} data-testid={`outreach-row-${c.id}`} className="border-t" style={{ borderColor: "var(--sj-border)", opacity: hist ? 0.85 : 1 }}>
                  <td className="py-3.5 px-4 font-semibold" style={{ color: "var(--sj-text)" }}>{c.name}</td>
                  <td className="py-3.5 px-4" style={{ color: "var(--sj-text-2)" }}>
                    <div>{c.phone ? <PhoneActions phone={c.phone} className="text-sm" /> : "—"}</div>
                    <div className="text-xs" style={{ color: "var(--sj-text-3)" }}>{c.email || ""}</div>
                  </td>
                  <td className="py-3.5 px-4" style={{ color: "var(--sj-text-2)" }}>{c.how_heard || "—"}</td>
                  {callStage && (
                    <td className="py-3.5 px-4">
                      {c.assigned_to_name ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium" style={{ background: "#0B5E4E1A", color: "var(--sj-primary)" }} data-testid={`outreach-assigned-${c.id}`}>
                          <UserCog className="h-3 w-3" /> {c.assigned_to_name}
                        </span>
                      ) : (
                        <span className="text-xs" style={{ color: "var(--sj-text-3)" }}>Unassigned</span>
                      )}
                    </td>
                  )}
                  <td className="py-3.5 px-4 text-xs" style={{ color: "var(--sj-text-3)" }}>{c.added_by || "—"}{c.source === "self" ? " (self)" : ""}</td>
                  <td className="py-3.5 px-4">
                    {hist ? (
                      <span data-testid={`outreach-nowin-${c.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5" style={{ background: "var(--sj-bg)", color: "var(--sj-text-2)", border: "1px solid var(--sj-border)" }}>
                        <History className="h-3 w-3" /> Now in: {TAB_META[c.stage]?.full || "Pastoral"}
                      </span>
                    ) : (callStage || tab === "evangelism") && c.contacted ? (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium" style={{ color: "var(--sj-primary)" }}><CheckCircle2 className="h-3.5 w-3.5" /> Called</span>
                    ) : (
                      <div className="flex flex-col gap-1">
                        <span className="text-xs" style={{ color: "var(--sj-text-3)" }}>{callStage ? "Awaiting call" : "New contact"}</span>
                        {c.overdue && <span data-testid={`outreach-overdue-${c.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 w-fit" style={{ background: "#FBEAD7", color: "#B45309" }}><Clock className="h-3 w-3" /> {c.days_waiting}d overdue</span>}
                      </div>
                    )}
                  </td>
                  <td className="py-3.5 px-4">
                    <div className="flex items-center justify-end gap-1.5">
                      {hist ? (
                        <span className="text-xs italic pr-1" style={{ color: "var(--sj-text-3)" }}>Handed over — reference only</span>
                      ) : (<>
                      {tab === "evangelism" && (<>
                        {!c.contacted && (
                          <button data-testid={`outreach-call-${c.id}`} onClick={() => { setCallFor(c); setCallNote(""); }}
                            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold hover:bg-[var(--sj-peach)]" style={{ color: "var(--sj-primary)", border: "1px solid var(--sj-border)" }}>
                            <PhoneCall className="h-3.5 w-3.5" /> Log call
                          </button>
                        )}
                        <button data-testid={`outreach-visited-${c.id}`} onClick={() => act(`/outreach/${c.id}/visited`, "Moved to Evangelism Follow-up")}
                          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold hover:bg-[var(--sj-peach)]" style={{ color: "var(--sj-primary)", border: "1px solid var(--sj-border)" }}>
                          <ArrowRightCircle className="h-3.5 w-3.5" /> To Follow-up
                        </button>
                      </>)}
                      {tab === "visitor_care" && (
                        <button data-testid={`outreach-to-followup-${c.id}`} onClick={() => moveContact(c, "visitor_follow_up")}
                          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold hover:bg-[var(--sj-peach)]" style={{ color: "var(--sj-primary)", border: "1px solid var(--sj-border)" }}>
                          <ArrowRightCircle className="h-3.5 w-3.5" /> To Follow-up
                        </button>
                      )}
                      {callStage && (
                        <button data-testid={`outreach-assign-${c.id}`} onClick={() => setAssignFor(c)}
                          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold hover:bg-[var(--sj-bg)]" style={{ color: "var(--sj-primary)", border: "1px solid var(--sj-border)" }}>
                          <UserCog className="h-3.5 w-3.5" /> Assign
                        </button>
                      )}
                      {callStage && !c.contacted && (
                        <button data-testid={`outreach-call-${c.id}`} onClick={() => { setCallFor(c); setCallNote(""); }}
                          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold hover:bg-[var(--sj-peach)]" style={{ color: "var(--sj-primary)", border: "1px solid var(--sj-border)" }}>
                          <PhoneCall className="h-3.5 w-3.5" /> Log call
                        </button>
                      )}
                      <button data-testid={`outreach-handoff-${c.id}`} onClick={() => toPastoral(c)}
                        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-white hover:brightness-105" style={{ background: "var(--sj-primary)" }}>
                        <HeartHandshake className="h-3.5 w-3.5" /> To Pastoral Care
                      </button>
                      <button data-testid={`outreach-edit-${c.id}`} onClick={() => setForm({ mode: "edit", data: withContactType(c) })} className="p-2 rounded-lg hover:bg-[var(--sj-bg)]" title="Edit"><Pencil className="h-4 w-4" style={{ color: "var(--sj-text-2)" }} /></button>
                      <button data-testid={`outreach-delete-${c.id}`} onClick={() => setConfirmDel(c)} className="p-2 rounded-lg hover:bg-red-50" title="Delete"><Trash2 className="h-4 w-4 text-red-500" /></button>
                      </>)}
                    </div>
                  </td>
                </tr>
              ); })}
            </tbody>
          </table>
        </div>
        <Paginator page={rowsPaged.page} pageCount={rowsPaged.pageCount} onPage={rowsPaged.setPage} testid="outreach-paginator" />
      </div>
      </>)}

      {view === "board" && (
        <div className="overflow-x-auto pb-2" data-testid="outreach-board">
          <div className="flex gap-4 min-w-max">
            {[...stages, ...(showPastoral ? ["handed_off"] : [])].map((col) => {
              const colContacts = contacts.filter((c) => c.stage === col)
                .filter((c) => !q.trim() || [c.name, c.phone, c.email].some((v) => (v || "").toLowerCase().includes(q.toLowerCase())));
              const isCallCol = ["evangelism_follow_up", "visitor_care", "visitor_follow_up"].includes(col);
              return (
                <div key={col} data-testid={`board-col-${col}`}
                  onDragOver={(e) => { e.preventDefault(); setDragOverCol(col); }}
                  onDragLeave={() => setDragOverCol((p) => (p === col ? null : p))}
                  onDrop={(e) => { e.preventDefault(); setDragOverCol(null); const c = contacts.find((x) => x.id === dragId); if (c) moveContact(c, col); setDragId(null); }}
                  className="w-72 shrink-0 rounded-2xl p-3 transition-colors"
                  style={{ background: dragOverCol === col ? "#0B5E4E14" : "var(--sj-bg)", border: dragOverCol === col ? "2px dashed var(--sj-primary)" : "2px solid transparent" }}>
                  <div className="flex items-center justify-between px-1 mb-3">
                    <h3 className="font-head text-sm font-bold uppercase tracking-wide" style={{ color: "var(--sj-text)" }}>{TAB_META[col]?.full || col}</h3>
                    <span className="text-xs font-bold rounded-full px-2 py-0.5" style={{ background: "#fff", color: "var(--sj-text-3)", border: "1px solid var(--sj-border)" }}>{colContacts.length}</span>
                  </div>
                  <div className="space-y-2.5 min-h-[60px]">
                    {colContacts.map((c) => (
                      <div key={c.id} data-testid={`board-card-${c.id}`} draggable
                        onDragStart={() => setDragId(c.id)} onDragEnd={() => { setDragId(null); setDragOverCol(null); }}
                        className={`${CARD} p-3 cursor-grab active:cursor-grabbing ${dragId === c.id ? "opacity-50" : ""}`}
                        style={c.overdue ? { borderColor: "#D97706", boxShadow: "0 0 0 1px #D9770655" } : {}}>
                        <div className="flex items-start gap-1.5">
                          <GripVertical className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "var(--sj-text-3)" }} />
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-sm truncate" style={{ color: "var(--sj-text)" }}>{c.name}</div>
                            {c.phone && <div className="text-xs truncate"><PhoneActions phone={c.phone} /></div>}
                            <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                              {c.assigned_to_name && <span className="inline-flex items-center gap-1 text-[11px] rounded-full px-2 py-0.5" style={{ background: "#0B5E4E1A", color: "var(--sj-primary)" }}><UserCog className="h-3 w-3" />{c.assigned_to_name}</span>}
                              {isCallCol && c.contacted && <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: "var(--sj-primary)" }}><CheckCircle2 className="h-3 w-3" /> Called</span>}
                              {c.overdue && <span data-testid={`board-overdue-${c.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5" style={{ background: "#FBEAD7", color: "#B45309" }}><Clock className="h-3 w-3" /> {c.days_waiting}d waiting</span>}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    {colContacts.length === 0 && <div className="text-xs text-center py-4" style={{ color: "var(--sj-text-3)" }}>Drop here</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Add / edit dialog */}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent data-testid="outreach-form-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl">{form?.mode === "edit" ? "Edit contact" : meta.addLabel}</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-3">
              <input data-testid="outreach-form-name" placeholder="Full name *" value={form.data.name} onChange={(e) => setForm({ ...form, data: { ...form.data, name: e.target.value } })} className={INPUT} />
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="outreach-form-phone" placeholder="Phone" value={form.data.phone} onChange={(e) => setForm({ ...form, data: { ...form.data, phone: e.target.value } })} className={INPUT} />
                <input data-testid="outreach-form-email" placeholder="Email" value={form.data.email} onChange={(e) => setForm({ ...form, data: { ...form.data, email: e.target.value } })} className={INPUT} />
              </div>
              <select data-testid="outreach-form-contact-type" required value={form.data.contact_type} onChange={(e) => setForm({ ...form, data: { ...form.data, contact_type: e.target.value } })} className={INPUT}>
                <option value="" disabled>Contact type *</option>
                {CONTACT_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              {form.data.contact_type === "other" && (
                <input data-testid="outreach-form-contact-type-other" required maxLength={120} placeholder="Type the contact type *" value={form.data.contact_type_other} onChange={(e) => setForm({ ...form, data: { ...form.data, contact_type_other: e.target.value } })} className={INPUT} />
              )}
              {form.mode === "add" && form.data.contact_type === "event_visitor" && (
                <p data-testid="outreach-form-event-visitor-note" className="text-xs rounded-lg px-3 py-2" style={{ background: "#FEF6D8", color: "#8a6a00" }}>Event visitors are added to Visitor Care Contacts and get a welcome text. They only join Pastoral Care when you move them.</p>
              )}
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Address" value={form.data.address} onChange={(e) => setForm({ ...form, data: { ...form.data, address: e.target.value } })} className={INPUT} />
                <input placeholder="Postcode" value={form.data.postcode} onChange={(e) => setForm({ ...form, data: { ...form.data, postcode: e.target.value } })} className={INPUT} />
              </div>
              <textarea placeholder="Notes" rows={2} value={form.data.notes} onChange={(e) => setForm({ ...form, data: { ...form.data, notes: e.target.value } })} className={INPUT} />
              <DialogFooter>
                <button type="submit" disabled={saving} data-testid="outreach-form-submit" className="rounded-full px-6 py-2.5 text-white font-semibold hover:brightness-105 disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>{saving ? "Saving..." : "Save contact"}</button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Log call dialog */}
      <Dialog open={!!callFor} onOpenChange={(o) => !o && setCallFor(null)}>
        <DialogContent data-testid="outreach-call-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl">{tab === "evangelism" ? "Log outreach call" : "Log welcome call"}</DialogTitle></DialogHeader>
          <p className="text-sm" style={{ color: "var(--sj-text-2)" }}>{tab === "evangelism"
            ? <>Recording an outreach call for <strong>{callFor?.name}</strong>. This notes that your evangelism team has spoken with them.</>
            : <>Recording a call for <strong>{callFor?.name}</strong>. This counts as the first of the pastoral team's two calls once handed off.</>}</p>
          <textarea data-testid="outreach-call-note" placeholder="How did the call go? (optional)" rows={3} value={callNote} onChange={(e) => setCallNote(e.target.value)} className={INPUT} />
          <DialogFooter>
            <button data-testid="outreach-call-submit" onClick={logCall} className="rounded-full px-6 py-2.5 text-white font-semibold hover:brightness-105" style={{ background: "var(--sj-primary)" }}>Save call</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign caller dialog */}
      <Dialog open={!!assignFor} onOpenChange={(o) => !o && setAssignFor(null)}>
        <DialogContent data-testid="outreach-assign-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl">Assign a caller</DialogTitle></DialogHeader>
          <p className="text-sm" style={{ color: "var(--sj-text-2)" }}>Pick who will follow up with <strong>{assignFor?.name}</strong>. They'll be notified.</p>
          <div className="max-h-72 overflow-y-auto space-y-1.5 mt-1">
            {assignees.length === 0 && <p className="text-sm py-4 text-center" style={{ color: "var(--sj-text-3)" }}>{tab === "evangelism_follow_up" ? "No Evangelism Follow-up team members found." : "No visitor-care team members found."}</p>}
            {assignees.map((a) => (
              <button key={a.id} data-testid={`outreach-assign-option-${a.id}`} onClick={() => assign(a.id)}
                className="w-full flex items-center justify-between rounded-xl px-4 py-3 text-sm font-medium hover:bg-[var(--sj-bg)] transition-colors" style={{ border: "1px solid var(--sj-border)", color: "var(--sj-text)" }}>
                <span className="inline-flex items-center gap-2"><UserCog className="h-4 w-4" style={{ color: "var(--sj-primary)" }} /> {a.name}</span>
                <span className="inline-flex items-center gap-2">
                  <span className="rounded-full px-2 py-0.5 text-xs font-semibold" style={{ background: "var(--sj-bg)", color: "var(--sj-text-2)" }}>{a.assigned_count || 0} in care</span>
                  {assignFor?.assigned_to_id === a.id && <CheckCircle2 className="h-4 w-4" style={{ color: "var(--sj-primary)" }} />}
                </span>
              </button>
            ))}
          </div>
          {assignFor?.assigned_to_id && (
            <DialogFooter>
              <button data-testid="outreach-unassign" onClick={() => assign("")} className="rounded-full px-5 py-2 text-sm font-semibold" style={{ color: "var(--sj-text-2)", border: "1px solid var(--sj-border)" }}>Clear assignment</button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Connect QR dialog */}
      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent data-testid="outreach-qr-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl">{qrTitle} QR code</DialogTitle></DialogHeader>
          <p className="text-sm" style={{ color: "var(--sj-text-2)" }}>Print this and take it out on outreach. When someone scans it, they land on your public sign-up page.</p>
          <div className="flex flex-col items-center gap-3 py-2" ref={qrRef}>
            <div className="p-4 rounded-2xl" style={{ border: "3px solid var(--sj-primary)" }}>
              <QRCodeCanvas value={connectQrUrl} size={220} level="M" includeMargin={false} fgColor="#0B5E4E" />
            </div>
            <span className="text-sm font-semibold" style={{ color: "var(--sj-primary)" }}>{connectUrl.replace(/^https?:\/\//, "")}</span>
          </div>
          <DialogFooter>
            <button data-testid="outreach-qr-print" onClick={printQr} className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-white font-semibold hover:brightness-105" style={{ background: "var(--sj-primary)" }}>
              <Printer className="h-4 w-4" /> Print poster
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent data-testid="outreach-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this contact?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently remove {confirmDel?.name} from the outreach list.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700" data-testid="outreach-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
